import React, { useState, useEffect, KeyboardEvent } from 'react'
import { Autocomplete, TextField, CircularProgress } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useSelector } from 'react-redux'
import { getPnrUser } from '../../../store/common/common.selectors'
import { CoreRootState } from '../../../store/coreRoot.reducer'

export type DropDownElement = {
  key: string
  value: string
}

type SearchableDropDownProps = {
  label?: string
  value?: string
  options: DropDownElement[]
  onChange?: (selected: DropDownElement) => void
  onSearch?: (typedValue: string) => 'fetch' | 'link' | undefined
  disableClearable?: boolean
  size?: 'small' | 'medium'
  onOpen?: () => void
}

const SearchableDropDownComponent: React.FC<SearchableDropDownProps> = ({
  label,
  value,
  options,
  onChange,
  onSearch,
  disableClearable,
  size,
  onOpen,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searching, setSearching] = useState(false)
  const [localSelectedKey, setLocalSelectedKey] = useState<string | undefined>(value)
  const [inputValue, setInputValue] = useState('')
  const [previousValue, setPreviousValue] = useState('')
  const [loading, setLoading] = useState(false)
  const pnrError = useSelector((state: CoreRootState) => state.commonStore.error?.getUserByPnr)
  const addUserError = useSelector((state: CoreRootState) => state.commonStore.error?.addUserPageRelation)

  useEffect(() => {
    if (!searching) {
      setLocalSelectedKey(value)
    }
  }, [value, searching])

  useEffect(() => {
    if (!localSelectedKey) return
    const found = options.find((o) => o.key === localSelectedKey)
    if (found) {
      setInputValue(found.value)
      setPreviousValue(found.value)
    }
  }, [localSelectedKey, options])

  useEffect(() => {
    if (loading && (pnrError || addUserError)) {
      setLoading(false)
    }
  }, [pnrError, addUserError, loading])

  const handleSearch = () => {
    if (!onSearch || !inputValue.trim()) return
    setLoading(true)
    const result = onSearch(inputValue.trim())
    if (result === 'link') {
      setTimeout(() => {
        setLoading(false)
      }, 1000)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSearch()
    }
  }

  const handleSearchClick = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation()
    handleSearch()
  }

  const autoCompleteValue = options.find((opt) => opt.key === localSelectedKey) || null

  return (
    <Autocomplete
      forcePopupIcon
      popupIcon={
        isOpen ? (
          <SearchIcon sx={{ color: 'action.active', transform: 'rotate(180deg)' }} onClick={handleSearchClick}/>
        ) : (
          <ArrowDropDownIcon sx={{ color: 'action.active', transform: 'none !important' }} />
        )
      }
      freeSolo
      disableClearable={disableClearable}
      size={size}
      onOpen={() => {
        setIsOpen(true)
        setSearching(true)
        setInputValue('')
        onOpen?.()
      }}
      onClose={() => {
        setIsOpen(false)
        if (!inputValue) setInputValue(previousValue)
        setSearching(false)
        setLoading(false)
      }}
      options={options}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.value)}
      value={autoCompleteValue}
      onChange={(_, newValue) => {
        if (!newValue) return
        if (typeof newValue !== 'string') {
          setLocalSelectedKey(newValue.key)
          onChange?.(newValue)
          setSearching(false)
        }
      }}
      inputValue={inputValue}
      onInputChange={(_, newVal) => {
        const numericVal = newVal.replace(/\D/g, '')
        setInputValue(numericVal)
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Personalnummer eingeben"
          onKeyDown={handleKeyDown}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default SearchableDropDownComponent
